import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type JoinResult } from '../lib/api'
import { Avatar, ErrorText, MIN_PASSWORD_LENGTH, PasswordHint, Sheet } from '../components/ui'

export function Join() {
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const [code, setCode] = useState(() => params.get('code') ?? '')
  const [joined, setJoined] = useState<JoinResult | null>(null)

  const join = useMutation({
    mutationFn: () => api.join(code),
    onSuccess: (result) => setJoined(result),
  })

  if (joined) {
    return <PickProfile joined={joined} onDone={() => queryClient.invalidateQueries()} />
  }

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
      <h1 className="title" style={{ fontSize: '1.7rem' }}>
        Join your household
      </h1>
      <p className="subtitle">
        Ask the parent who set up the household for an invite code, then add your own login.
      </p>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          join.mutate()
        }}
      >
        <div className="field">
          <label className="label" htmlFor="join-code">
            Invite code
          </label>
          <input
            id="join-code"
            className="input tabular"
            autoCapitalize="characters"
            autoComplete="one-time-code"
            spellCheck={false}
            style={{ fontSize: '1.4rem', letterSpacing: '0.18em', textAlign: 'center' }}
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="K7QF HA2Z"
          />
        </div>

        <ErrorText error={join.error} />

        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          disabled={code.trim().length < 4 || join.isPending}
        >
          Join
        </button>
      </form>

      <Link to="/" className="btn btn-quiet btn-block">
        Back
      </Link>
    </div>
  )
}

function PickProfile({ joined, onDone }: { joined: JoinResult; onDone: () => void }) {
  const [claiming, setClaiming] = useState<{ id: string; name: string } | null>(null)

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
      <div className="stack" style={{ textAlign: 'center' }}>
        <h1 className="title" style={{ fontSize: '1.6rem' }}>
          Welcome to {joined.householdName}
        </h1>
        <p className="subtitle">Set up your login.</p>
      </div>

      <div className="list">
        {joined.members.map((member) => (
          <button
            key={member.id}
            type="button"
            className="check"
            onClick={() => setClaiming({ id: member.id, name: member.name })}
          >
            <Avatar emoji={member.emoji} color={member.color} ring />
            <span className="check-body">
              <span className="check-title">{member.name}</span>
              <span className="check-detail">Add an email and password</span>
            </span>
          </button>
        ))}
      </div>

      {joined.members.length === 0 ? (
        <p className="subtitle">
          There is no profile waiting for you. Ask a parent to add you, then use the code again.
        </p>
      ) : null}

      <ClaimSheet member={claiming} onClose={() => setClaiming(null)} onDone={onDone} />
    </div>
  )
}

function ClaimSheet({
  member,
  onClose,
  onDone,
}: {
  member: { id: string; name: string } | null
  onClose: () => void
  onDone: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')

  const claim = useMutation({
    mutationFn: () =>
      api.claimParentProfile({ memberId: member?.id ?? '', email: email.trim(), password, pin }),
    onSuccess: onDone,
  })

  return (
    <Sheet open={member !== null} title={`Set up ${member?.name ?? ''}`} onClose={onClose}>
      <p className="subtitle">
        Parents sign in with an email and password so the account can be recovered. You'll also need the
        household PIN to prove you're one of the parents.
      </p>

      <div className="field">
        <label className="label" htmlFor="claim-email">
          Email
        </label>
        <input
          id="claim-email"
          className="input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="claim-password">
          Password
        </label>
        <input
          id="claim-password"
          className="input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <PasswordHint password={password} />
      </div>

      <div className="field">
        <label className="label" htmlFor="claim-pin">
          Household PIN
        </label>
        <input
          id="claim-pin"
          className="input tabular"
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
        />
      </div>

      <ErrorText error={claim.error} />

      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!email.trim() || password.length < MIN_PASSWORD_LENGTH || pin.length < 4 || claim.isPending}
        onClick={() => claim.mutate()}
      >
        Create my login
      </button>
    </Sheet>
  )
}
